// Tiny dict-based i18n (no dependency). ko (default) + en catalogs.
// Legal/compliance copy lives under the `compliance.*` namespace so it stays
// consistent across screens (AI 고지, deepfake label, spoiler dialog, consent).
// 00 §7.1 / §7.3, 04-web §3.

export type Lang = "ko" | "en";

const LANG_KEY = "shortdrama.lang";

type Catalog = Record<string, string>;

const ko: Catalog = {
  // nav / chrome
  "nav.feed": "피드",
  "nav.shorts": "쇼츠",
  "nav.search": "검색",
  "nav.upload": "업로드",
  "nav.login": "로그인",
  "nav.me": "내 정보",
  "nav.lang": "EN",

  // common
  "common.loading": "불러오는 중…",
  "common.cancel": "취소",
  "common.retry": "다시 시도",
  "common.watch": "시청",
  "common.play": "재생",
  "common.search": "검색",
  "common.searching": "검색 중…",
  "common.notFound": "페이지를 찾을 수 없습니다.",
  "common.loadFailed": "불러오기 실패: {msg}",
  "common.openMenu": "메뉴 열기",
  "common.close": "닫기",
  "common.skipToContent": "본문으로 건너뛰기",
  "common.primaryNav": "주요 메뉴",

  // feed / title
  "feed.trending": "트렌딩",
  "feed.empty": "아직 공개된 작품이 없습니다.",
  "feed.loadFailed": "불러오기 실패: {msg}",
  "kind.drama": "드라마",
  "kind.movie": "영화",
  "title.episodes": "회차",
  "title.episodeLabel": "{idx}화 {name}",
  "title.noCut": "재생 가능한 컷이 없습니다.",
  "title.cutLoadFailed": "컷을 불러오지 못했습니다.",

  // player
  "player.loading": "플레이어 로딩…",
  "player.cutLoadFailed": "컷 로드 실패: {msg}",
  "player.manifestError": "재생 오류(매니페스트/Stream 미설정 가능)",
  "player.hlsUnsupported": "이 브라우저는 HLS를 지원하지 않습니다.",

  // shorts
  "shorts.loading": "쇼츠 로딩…",
  "shorts.empty": "쇼츠가 없습니다.",
  "shorts.loadFailed": "쇼츠 로드 실패: {msg}",
  "shorts.pagerLabel": "쇼츠 피드",
  "shorts.openShort": "쇼츠 재생",

  // search
  "search.title": "검색",
  "search.placeholder": "검색어를 입력하세요",
  "search.prompt": "검색어를 입력하세요.",
  "search.noResults": "결과가 없습니다.",
  "search.failed": "검색 실패: {msg}",

  // auth / login
  "login.tagline": "AI가 제작한 드라마/영화를 감상하고, 직접 편집해 더 나은 버전으로 진화시키는 플랫폼.",
  "login.google": "Google로 계속",
  "login.apple": "Apple로 계속",
  "login.devSection": "로컬 개발용 로그인",
  "login.devHandle": "dev handle (로컬 테스트)",
  "login.devButton": "dev-login",
  "login.handleRequired": "handle을 입력하세요.",
  "login.failed": "로그인 실패: {msg}",
  "callback.processing": "로그인 처리 중…",
  "callback.failedTitle": "로그인 실패",
  "callback.providerError": "provider 오류: {error}",
  "callback.noCode": "인증 코드가 없습니다.",
  "callback.verifierExpired": "PKCE verifier가 만료되었습니다. 다시 로그인하세요.",
  "callback.stateMismatch": "state 불일치(CSRF 방지). 다시 로그인하세요.",
  "callback.exchangeFailed": "토큰 교환 실패: {msg}",
  "callback.reconsent": "약관이 변경되었습니다. 재동의가 필요합니다.",

  // library / profile
  "library.myCuts": "내 컷",
  "library.myVariants": "내 variant",
  "library.noCuts": "아직 만든 컷이 없습니다.",
  "library.noVariants": "아직 만든 variant가 없습니다.",
  "library.profileFailed": "프로필을 불러오지 못했습니다.",
  "profile.loading": "프로필 불러오는 중…",
  "profile.notFound": "사용자를 찾을 수 없습니다.",
  "profile.cuts": "컷 {n}",
  "profile.variants": "variant {n}",
  "profile.followers": "팔로워 {n}",

  // editor
  "editor.title": "구간 편집기",
  "editor.intro": "회차 {id} — 임의 구간 [start_ms, end_ms)를 포크해 대체 편집본을 만듭니다.",
  "editor.missingEpisode": "회차 정보가 없습니다. 작품 화면에서 회차를 선택해 진입하세요.",
  "editor.region": "구간 선택",
  "editor.start": "시작(ms)",
  "editor.end": "끝(ms)",
  "editor.total": "전체 길이(ms)",
  "editor.labelField": "라벨",
  "editor.labelPlaceholder": "변형 라벨 (예: 느와르 버전)",
  "editor.selectedRange": "선택 구간: {start} – {end}ms ({dur}ms)",
  "editor.invalidRange": "유효한 구간 [start_ms, end_ms)를 입력하세요 (end > start).",
  "editor.fork": "포크 생성 (draft)",
  "editor.forking": "포크 생성 중…",
  "editor.forked": "포크 생성됨 ({id}). AI 라벨을 표기한 뒤 게시하세요.",
  "editor.forkFailed": "포크 실패: {msg}",
  "editor.publish": "게시 (commit)",
  "editor.publishing": "게시 중…",
  "editor.published": "게시 완료.",
  "editor.publishFailed": "게시 실패: {msg}",
  "editor.processing": "게시됨. 파이프라인 처리 중…",
  "editor.jobs": "잡 상태",
  "editor.jobsFailed": "잡 폴링 실패: {msg}",
  "editor.pipelineDone": "파이프라인 종료.",
  "editor.progress": "처리 진행: {done}/{total}",
  "editor.step.region": "구간 선택",
  "editor.step.fork": "포크 생성",
  "editor.step.publish": "게시",
  "editor.step.process": "처리",

  // upload
  "upload.title": "업로드 / 생성",
  "upload.intro": "작품·회차를 만들고 영상을 Stream에 직접 업로드합니다.",
  "upload.sectionTitle": "작품",
  "upload.sectionEpisode": "회차",
  "upload.titleField": "제목",
  "upload.titlePlaceholder": "작품 제목",
  "upload.kind": "종류",
  "upload.synopsis": "줄거리",
  "upload.synopsisPlaceholder": "줄거리(선택)",
  "upload.epIdx": "회차 순번",
  "upload.epName": "회차 이름",
  "upload.epNamePlaceholder": "회차 이름 (예: 1화)",
  "upload.duration": "정규 길이(ms)",
  "upload.durationPlaceholder": "정규 길이(ms)",
  "upload.file": "영상 파일",
  "upload.submit": "업로드 및 생성",
  "upload.titleRequired": "작품 제목을 입력하세요.",
  "upload.durationRequired": "정규 길이(ms)를 입력하세요.",
  "upload.fileRequired": "업로드할 영상 파일을 선택하세요.",
  "upload.creatingTitle": "작품 생성 중…",
  "upload.creatingEpisode": "회차 생성 중…",
  "upload.signing": "업로드 URL 발급 중…",
  "upload.uploading": "Stream으로 직접 업로드 중… {pct}%",
  "upload.uploadFailed": "업로드 실패 {status}",
  "upload.ingesting": "ingest 등록 중…",
  "upload.done": "업로드 완료. 처리(트랜스코드/모더레이션) 대기 중…",
  "upload.failed": "실패: {msg}",
  "upload.openTitle": "작품 보기",
  "upload.openEditor": "이 회차 편집하기",

  // compliance / legal (00 §7.1, §7.3, §12.2). Keep consistent across screens.
  "compliance.aiBadge": "AI",
  "compliance.aiGenerated": "AI 생성",
  "compliance.aiEdited": "AI 편집",
  "compliance.deepfake": "딥페이크/AI 합성",
  "compliance.spoilerBadge": "스포일러",
  "compliance.spoilerTitle": "스포일러 경고",
  "compliance.spoilerDialog": "이 쇼츠는 스포일러가 포함되어 있습니다. 시청하시겠습니까?",
  "compliance.spoilerAlwaysOff": "스포일러 경고 항상 끄기",
  "compliance.aiNotice": "본 서비스는 AI 생성·편집 콘텐츠를 다룹니다. 업로드물은 서비스 개선·AI 학습에 사용될 수 있으며 설정에서 거부할 수 있습니다.",
  "compliance.productionPanel": "제작 방식",
  "compliance.productionNone": "표기된 AI 제작 정보가 없습니다.",
  "compliance.deepfakeFullVideo": "딥페이크/합성 표기는 영상 전체 구간에 적용됩니다 (00 §7.1).",
  "compliance.sectionTitle": "AI 표기 / 동의 (필수)",
  "compliance.labelHeading": "AI 라벨 (게시 전 필수)",
  "compliance.checkGenerated": "AI로 생성된 콘텐츠입니다 (ai_generated)",
  "compliance.checkEdited": "AI로 편집된 콘텐츠입니다 (ai_edited)",
  "compliance.checkDeepfake": "실존 인물의 딥페이크/합성이 포함됩니다 (deepfake)",
  "compliance.trainingConsent": "서비스 개선·AI 학습에 이 콘텐츠 사용을 허용합니다 (학습 라이선스, opt-in)",
  "compliance.consentAttestLabel": "딥페이크/음성 동의 진술",
  "compliance.consentAttestPlaceholder": "딥페이크/음성 동의 진술 (실존 인물·성우 음성 포함 시 필수, 증빙 참조)",
  "compliance.labelRequiredPublish": "게시 전 AI 라벨(생성/편집/딥페이크)을 표기해야 합니다 (00 §7.3).",
  "compliance.labelRequiredUpload": "AI 생성/편집/딥페이크 여부를 표기해야 합니다 (00 §7.3).",
  "compliance.deepfakeConsentRequired": "딥페이크/합성에는 동의 진술이 필수입니다 (성폭력처벌법·§7.3).",
};

const en: Catalog = {
  "nav.feed": "Feed",
  "nav.shorts": "Shorts",
  "nav.search": "Search",
  "nav.upload": "Upload",
  "nav.login": "Sign in",
  "nav.me": "My page",
  "nav.lang": "KO",

  "common.loading": "Loading…",
  "common.cancel": "Cancel",
  "common.retry": "Try again",
  "common.watch": "Watch",
  "common.play": "Play",
  "common.search": "Search",
  "common.searching": "Searching…",
  "common.notFound": "Page not found.",
  "common.loadFailed": "Failed to load: {msg}",
  "common.openMenu": "Open menu",
  "common.close": "Close",
  "common.skipToContent": "Skip to content",
  "common.primaryNav": "Primary",

  "feed.trending": "Trending",
  "feed.empty": "No published titles yet.",
  "feed.loadFailed": "Failed to load: {msg}",
  "kind.drama": "Drama",
  "kind.movie": "Movie",
  "title.episodes": "Episodes",
  "title.episodeLabel": "Ep.{idx} {name}",
  "title.noCut": "No playable cut available.",
  "title.cutLoadFailed": "Could not load cuts.",

  "player.loading": "Loading player…",
  "player.cutLoadFailed": "Failed to load cut: {msg}",
  "player.manifestError": "Playback error (manifest/Stream may be unconfigured)",
  "player.hlsUnsupported": "This browser does not support HLS.",

  "shorts.loading": "Loading shorts…",
  "shorts.empty": "No shorts available.",
  "shorts.loadFailed": "Failed to load shorts: {msg}",
  "shorts.pagerLabel": "Shorts feed",
  "shorts.openShort": "Play short",

  "search.title": "Search",
  "search.placeholder": "Enter a search term",
  "search.prompt": "Enter a search term.",
  "search.noResults": "No results.",
  "search.failed": "Search failed: {msg}",

  "login.tagline": "Watch AI-produced dramas and films, then edit them into better versions.",
  "login.google": "Continue with Google",
  "login.apple": "Continue with Apple",
  "login.devSection": "Local development login",
  "login.devHandle": "dev handle (local test)",
  "login.devButton": "dev-login",
  "login.handleRequired": "Please enter a handle.",
  "login.failed": "Sign-in failed: {msg}",
  "callback.processing": "Signing in…",
  "callback.failedTitle": "Sign-in failed",
  "callback.providerError": "provider error: {error}",
  "callback.noCode": "Missing authorization code.",
  "callback.verifierExpired": "PKCE verifier expired. Please sign in again.",
  "callback.stateMismatch": "state mismatch (CSRF protection). Please sign in again.",
  "callback.exchangeFailed": "Token exchange failed: {msg}",
  "callback.reconsent": "Terms have changed. Re-consent is required.",

  "library.myCuts": "My cuts",
  "library.myVariants": "My variants",
  "library.noCuts": "You haven't created any cuts yet.",
  "library.noVariants": "You haven't created any variants yet.",
  "library.profileFailed": "Could not load profile.",
  "profile.loading": "Loading profile…",
  "profile.notFound": "User not found.",
  "profile.cuts": "Cuts {n}",
  "profile.variants": "Variants {n}",
  "profile.followers": "Followers {n}",

  "editor.title": "Region editor",
  "editor.intro": "Episode {id} — fork an arbitrary region [start_ms, end_ms) into an alternate edit.",
  "editor.missingEpisode": "No episode selected. Open the editor from a title's episode list.",
  "editor.region": "Region selection",
  "editor.start": "Start (ms)",
  "editor.end": "End (ms)",
  "editor.total": "Total length (ms)",
  "editor.labelField": "Label",
  "editor.labelPlaceholder": "Variant label (e.g. Noir version)",
  "editor.selectedRange": "Selected region: {start} – {end}ms ({dur}ms)",
  "editor.invalidRange": "Enter a valid region [start_ms, end_ms) (end > start).",
  "editor.fork": "Create fork (draft)",
  "editor.forking": "Creating fork…",
  "editor.forked": "Fork created ({id}). Add AI labels, then publish.",
  "editor.forkFailed": "Fork failed: {msg}",
  "editor.publish": "Publish (commit)",
  "editor.publishing": "Publishing…",
  "editor.published": "Published.",
  "editor.publishFailed": "Publish failed: {msg}",
  "editor.processing": "Published. Pipeline processing…",
  "editor.jobs": "Job status",
  "editor.jobsFailed": "Job poll failed: {msg}",
  "editor.pipelineDone": "Pipeline finished.",
  "editor.progress": "Processing: {done}/{total}",
  "editor.step.region": "Region",
  "editor.step.fork": "Fork",
  "editor.step.publish": "Publish",
  "editor.step.process": "Process",

  "upload.title": "Upload / Create",
  "upload.intro": "Create a title and episode, then upload the video directly to Stream.",
  "upload.sectionTitle": "Title",
  "upload.sectionEpisode": "Episode",
  "upload.titleField": "Title",
  "upload.titlePlaceholder": "Title name",
  "upload.kind": "Kind",
  "upload.synopsis": "Synopsis",
  "upload.synopsisPlaceholder": "Synopsis (optional)",
  "upload.epIdx": "Episode index",
  "upload.epName": "Episode name",
  "upload.epNamePlaceholder": "Episode name (e.g. Ep.1)",
  "upload.duration": "Canonical length (ms)",
  "upload.durationPlaceholder": "Canonical length (ms)",
  "upload.file": "Video file",
  "upload.submit": "Upload & create",
  "upload.titleRequired": "Please enter a title.",
  "upload.durationRequired": "Please enter the canonical length (ms).",
  "upload.fileRequired": "Please choose a video file to upload.",
  "upload.creatingTitle": "Creating title…",
  "upload.creatingEpisode": "Creating episode…",
  "upload.signing": "Requesting upload URL…",
  "upload.uploading": "Uploading directly to Stream… {pct}%",
  "upload.uploadFailed": "Upload failed {status}",
  "upload.ingesting": "Registering ingest…",
  "upload.done": "Upload complete. Awaiting processing (transcode/moderation)…",
  "upload.failed": "Failed: {msg}",
  "upload.openTitle": "View title",
  "upload.openEditor": "Edit this episode",

  "compliance.aiBadge": "AI",
  "compliance.aiGenerated": "AI generated",
  "compliance.aiEdited": "AI edited",
  "compliance.deepfake": "Deepfake / AI synthesis",
  "compliance.spoilerBadge": "Spoiler",
  "compliance.spoilerTitle": "Spoiler warning",
  "compliance.spoilerDialog": "이 쇼츠는 스포일러가 포함되어 있습니다. 시청하시겠습니까?",
  "compliance.spoilerAlwaysOff": "Always turn off spoiler warnings",
  "compliance.aiNotice": "This service handles AI-generated/edited content. Uploads may be used to improve the service and train AI; you can opt out in settings.",
  "compliance.productionPanel": "Production method",
  "compliance.productionNone": "No AI production info disclosed.",
  "compliance.deepfakeFullVideo": "Deepfake/synthesis labels apply to the entire video (00 §7.1).",
  "compliance.sectionTitle": "AI disclosure / consent (required)",
  "compliance.labelHeading": "AI labels (required before publish)",
  "compliance.checkGenerated": "This content is AI-generated (ai_generated)",
  "compliance.checkEdited": "This content is AI-edited (ai_edited)",
  "compliance.checkDeepfake": "Contains a deepfake/synthesis of a real person (deepfake)",
  "compliance.trainingConsent": "Allow this content to be used for service improvement and AI training (training license, opt-in)",
  "compliance.consentAttestLabel": "Deepfake / voice consent attestation",
  "compliance.consentAttestPlaceholder": "Deepfake/voice consent attestation (required when a real person/voice actor is included; reference evidence)",
  "compliance.labelRequiredPublish": "AI labels (generated/edited/deepfake) must be disclosed before publishing (00 §7.3).",
  "compliance.labelRequiredUpload": "You must disclose AI generated/edited/deepfake status (00 §7.3).",
  "compliance.deepfakeConsentRequired": "A consent attestation is required for deepfakes/synthesis (Sexual Violence Punishment Act · §7.3).",
};

const catalogs: Record<Lang, Catalog> = { ko, en };

export function getLang(): Lang {
  return localStorage.getItem(LANG_KEY) === "en" ? "en" : "ko";
}

export function setLang(lang: Lang): void {
  localStorage.setItem(LANG_KEY, lang);
  // Strings are baked at render time; a reload re-renders every screen consistently.
  location.reload();
}

export function toggleLang(): void {
  setLang(getLang() === "ko" ? "en" : "ko");
}

/** Translate a key, falling back to ko then to the key itself. Supports {var} interpolation. */
export function t(key: string, vars?: Record<string, string | number>): string {
  const lang = getLang();
  let s = catalogs[lang][key] ?? ko[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return s;
}

/** Translate any element in the static document carrying a data-i18n key. */
export function applyStaticI18n(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    if (key) el.textContent = t(key);
  });
  // Translate aria-label attributes (data-i18n-aria-label="key").
  root.querySelectorAll<HTMLElement>("[data-i18n-aria-label]").forEach((el) => {
    const key = el.getAttribute("data-i18n-aria-label");
    if (key) el.setAttribute("aria-label", t(key));
  });
}
